import { Cylinder, Edges } from '@react-three/drei'
import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { Compass3D } from 'features/assetViz/objects/decor/compass/Compass3D'
import { Ruler3D } from 'features/assetViz/objects/decor/Ruler3D'
import { TankDecorGroup3D } from 'features/assetViz/objects/decor/TankDecorGroup3D'
import {
  createHalfShellAttributes,
  planeGeometry,
  xWrappedShellGeometry,
} from 'features/assetViz/utils/geometryDefines'
import { extractScale, Vec3 } from 'features/assetViz/utils/helpers'
import { RenderLayerID } from 'features/assetViz/utils/renderingDefines'
import React from 'react'
import * as THREE from 'three'
import { Color, Vector3 } from 'three'
import { GeckoAssetModel, GeckoLaunchTube, GeckoMeasurement } from 'types'

import { Surface3D, Surface3DGroupProps } from './Surface3D'
import { ComponentSurfaceWrappingMode } from './util/SurfaceProvider'

const siloCoverHeight = 0.5
const controlRoomHeightBound = 8
const controlRoomVerticalPadding = 0.5
const controlRoomSpaceRadius = 4
const thickness = 0.5
const bottomHeight = 0.5

/**
 * A surface representing a launch tube, plotted on a cartesian plane, with the
 * center of its base at "position" and decorated with Rich Asset features
 * specific to shells.
 * @param model               The tank shell component model
 * @param assetModel          The asset model for the asset that the Launch Tube is a part of
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */
export const LaunchTubeSurface3D: React.FC<
  {
    model: GeckoLaunchTube
    assetModel: GeckoAssetModel
  } & Surface3DGroupProps
> = ({ model, assetModel, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const { bounds, scale } = React.useMemo(() => {
    const scale = extractScale(groupProps.scale).multiply(
      show3D
        ? new Vector3(
            model.radius.num,
            model.componentBounds.height.num,
            model.radius.num
          )
        : new Vector3(
            model.componentBounds.width.num,
            model.componentBounds.height.num,
            1.0
          )
    )
    return {
      bounds: model.componentBounds,
      scale: scale,
    }
  }, [model, groupProps.scale, show3D])

  const geometry = React.useMemo(() => {
    if (!show3D) return planeGeometry
    return xWrappedShellGeometry
  }, [show3D])

  const getCompassPosition = React.useCallback<(angle: number) => Vec3>(
    (angle) => {
      const radians = (angle * Math.PI) / 180
      const fullCircle = 2 * Math.PI
      if (show3D) return [-Math.cos(radians), 1.0, Math.sin(radians)]
      const positionAlongWidth = model.componentBounds.width.scale(
        radians / fullCircle
      )
      const relativePositionAlongWidth = model.componentBounds.start.x
        .plus(positionAlongWidth)
        .scale(1.0 / model.componentBounds.width.num).num
      return [relativePositionAlongWidth, 1.0, 0]
    },
    [show3D, model]
  )

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relX = coord
        .convertTo(bounds.x.unit)
        .minus(bounds.start.x)
        .scale(1.0 / bounds.width.num).num
      if (!show3D) return [relX, 0, 0]
      const angle = 2.0 * Math.PI * relX
      return [-Math.cos(angle), 0, Math.sin(angle)]
    },
    [show3D, bounds]
  )

  const positionYTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relY = coord
        .convertTo(bounds.y.unit)
        .minus(bounds.start.y)
        .scale(1.0 / bounds.height.num).num
      if (!show3D) return [0, relY, 0]
      return [-0.875 * 1.3, relY, 0.5 * 1.3]
    },
    [show3D, bounds]
  )

  return (
    <>
      <TankDecorGroup3D yPivot={show3D}>
        <LaunchTubeEncasement3D model={model} />
      </TankDecorGroup3D>
      <Surface3D
        geometry={geometry}
        bounds={bounds}
        wrapMode={
          show3D
            ? ComponentSurfaceWrappingMode.ComponentWrapsX
            : ComponentSurfaceWrappingMode.UnrolledComponentWrapsX
        }
        componentModel={model}
        {...groupProps}
        // Apply the true scaling last (override anything in groupProps)
        scale={scale}>
        <TankDecorGroup3D yPivot={show3D}>
          <Ruler3D
            anchor={'right'}
            start={model.componentBounds.start.y}
            end={model.componentBounds.end.y}
            includeTicks
            includeStart
            includeEnd
            getTickPosition={positionYTick}
          />
        </TankDecorGroup3D>
        <TankDecorGroup3D>
          <Ruler3D
            anchor={show3D ? 'bottom' : 'top'}
            start={model.componentBounds.start.x}
            end={model.componentBounds.end.x}
            includeTicks
            includeStart
            includeEnd={!show3D}
            getTickPosition={positionXTick}
          />
        </TankDecorGroup3D>

        <Compass3D
          componentModel={model}
          assetModel={assetModel}
          getRotationPosition={getCompassPosition}
        />
      </Surface3D>
    </>
  )
}

export const LaunchTubeEncasement3D: React.FC<{
  model: GeckoLaunchTube
}> = ({ model }) => {
  const { show3D } = useAssetVizInternal()
  const {
    controlRoomHeight,
    controlRoomVerticalPaddingThickness,
    baseHeight,
    upperOuterRadius,
    lowerOuterRadius,
    chamberStartHeight,
  } = React.useMemo(() => {
    const buffer = 0.1
    const controlRoomStructureHeight = Math.min(
      controlRoomHeightBound,
      model.height.num
    )
    const controlRoomHeight =
      controlRoomStructureHeight - controlRoomVerticalPadding * 2

    const upperOuterRadius =
      model.radius.num + controlRoomSpaceRadius + thickness + buffer
    const controlRoomVerticalPaddingThickness =
      upperOuterRadius - model.radius.num - thickness - buffer

    const lowerOuterRadius = model.radius.num + buffer + thickness
    const baseHeight = model.height.num
    const chamberStartHeight = Math.max(
      model.height.num - controlRoomStructureHeight,
      0
    )
    return {
      controlRoomHeight,
      controlRoomVerticalPaddingThickness,
      baseHeight,
      upperOuterRadius,
      lowerOuterRadius,
      chamberStartHeight,
    }
  }, [model.height.num, model.radius.num])

  const { assetColorString } = useAssetVizInternal()

  const controlRoomBase = React.useMemo(() => {
    return createHalfShellAttributes(
      controlRoomVerticalPadding,
      upperOuterRadius,
      controlRoomVerticalPaddingThickness
    )
  }, [upperOuterRadius, controlRoomVerticalPaddingThickness])

  const controlRoom = React.useMemo(() => {
    return createHalfShellAttributes(
      controlRoomHeight,
      upperOuterRadius,
      thickness
    )
  }, [controlRoomHeight, upperOuterRadius])

  const baseShell = React.useMemo(() => {
    return baseHeight !== undefined
      ? createHalfShellAttributes(baseHeight, lowerOuterRadius, thickness)
      : undefined
  }, [baseHeight, lowerOuterRadius])

  const assetColor = React.useMemo(
    () => new Color(assetColorString).convertSRGBToLinear(),
    [assetColorString]
  )

  const renderLayer = React.useMemo(
    () =>
      show3D
        ? RenderLayerID.VisibleNonCollidable
        : RenderLayerID.InvisibleNonCollidable,
    [show3D]
  )

  return (
    <>
      <Cylinder
        layers={renderLayer}
        position={[
          0,
          chamberStartHeight +
            controlRoomHeight +
            2 * controlRoomVerticalPadding +
            siloCoverHeight / 2,
          0,
        ]}
        scale={[upperOuterRadius, siloCoverHeight, upperOuterRadius]}>
        <meshBasicMaterial attach='material' color={assetColor} />
        <Edges layers={renderLayer} />
      </Cylinder>
      <mesh
        position={[
          0,
          chamberStartHeight + controlRoomHeight + controlRoomVerticalPadding,
          0,
        ]}
        name={`${model.id}_controlRoomBase`}
        layers={renderLayer}
        geometry={controlRoomBase}>
        <meshBasicMaterial
          color={assetColor}
          side={THREE.DoubleSide}
          polygonOffset={true}
          polygonOffsetFactor={10}
          polygonOffsetUnits={10}
        />
        <Edges layers={renderLayer} />
      </mesh>
      <mesh
        position={[
          0,
          chamberStartHeight + controlRoomHeight + controlRoomVerticalPadding,
          0,
        ]}
        name={`${model.id}_controlRoomCeiling`}
        layers={renderLayer}
        geometry={controlRoomBase}>
        <meshBasicMaterial
          color={assetColor}
          side={THREE.DoubleSide}
          polygonOffset={true}
          polygonOffsetFactor={10}
          polygonOffsetUnits={10}
        />
        <Edges layers={renderLayer} />
      </mesh>
      <mesh
        position={[0, chamberStartHeight + controlRoomVerticalPadding, 0]}
        name={`${model.id}_controlRoom`}
        layers={renderLayer}
        geometry={controlRoom}>
        <meshBasicMaterial
          color={assetColor}
          side={THREE.DoubleSide}
          polygonOffset={true}
          polygonOffsetFactor={10}
          polygonOffsetUnits={10}
        />
        <Edges layers={renderLayer} />
      </mesh>
      <mesh
        position={[0, chamberStartHeight, 0]}
        name={`${model.id}_controlRoomFloor`}
        layers={renderLayer}
        geometry={controlRoomBase}>
        <meshBasicMaterial
          color={assetColor}
          side={THREE.DoubleSide}
          polygonOffset={true}
          polygonOffsetFactor={10}
          polygonOffsetUnits={10}
        />
        <Edges layers={renderLayer} />
      </mesh>

      <mesh name={`${model.id}_base`} layers={renderLayer} geometry={baseShell}>
        <meshBasicMaterial
          color={assetColor}
          side={THREE.DoubleSide}
          polygonOffset={true}
          polygonOffsetFactor={10}
          polygonOffsetUnits={10}
        />
        <Edges layers={renderLayer} />
      </mesh>
      <Cylinder
        layers={renderLayer}
        position={[0, -(bottomHeight / 2), 0]}
        scale={
          baseHeight <= 0
            ? [upperOuterRadius, bottomHeight, upperOuterRadius]
            : [lowerOuterRadius, bottomHeight, lowerOuterRadius]
        }>
        <meshBasicMaterial attach='material' color={assetColor} />
        <Edges layers={renderLayer} />
      </Cylinder>
    </>
  )
}
